import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from 'src/app/models/user';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, firstValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';


@Component({
  selector: 'app-script-repository',
  templateUrl: './script-repository.component.html',
  styleUrls: ['./script-repository.component.css'],
  standalone: true,
  imports: [FormsModule, CommonModule, TranslateModule]
})
export class ScriptRepositoryComponent implements OnInit {
  @Input() device: any;
  @Input() currentUser: User;
  @Input() Token: string;

  //alert-overlay
  @ViewChild('alerttemplate') alertTemplate!: TemplateRef<any>;
  modalRef!: MatDialogRef<any>;
  msg: string = "";

  baseUrl = environment.baseUrl;

  // selection of image or element scripts fetch
  scriptListsOf: 'image' | 'element' | 'both' = 'both';

  // Script Repository State
  isScriptsLoading: boolean = false;
  scriptsList: any[] = [];          // image scripts
  elementScriptsList: any[] = [];   // element scripts
  selectedScript: any = null;
  selectedScriptDetails: any = null;
  scriptSteps: any[] = [];
  executionLiveStatus: string = '';
  expandedStepIndex: number | null = null;
  loadedImages: { [key: string]: string } = {};
  zoomImageUrl: string | null = null;

  // Capabilities Editor State
  showCapabilitiesEditor: boolean = false;
  capEditAppName: string = '';
  capEditPackageName: string = '';
  capEditRawJson: string = '';
  capJsonError: string = '';
  deviceCapabilities: any = null;

  @ViewChild('capsModalTemplate') capsModalTemplate!: TemplateRef<any>;
  capsDialogRef: MatDialogRef<any> | null = null;

  // Script Editor State
  @ViewChild('editScriptModalTemplate') editScriptModalTemplate!: TemplateRef<any>;
  editScriptDialogRef: MatDialogRef<any> | null = null;
  editScriptName: string = '';
  editScriptMobile: string = '';
  editScriptStepsJson: string = '';
  editScriptJsonError: string = '';
  scriptBeingEdited: any = null;

  // Websocket & Execution reference
  private liveWs: WebSocket | null = null;
  currentExecutionId: string = '';
  tcSearchQuery: string = '';

  constructor(
    private http: HttpClient,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadScripts();
  }

  // --- List Loading (fetches BOTH sources in parallel, always) ---

  loadScripts() {
    this.isScriptsLoading = true;

    let params = new HttpParams();
    if (this.currentUser && this.currentUser.email) {
      params = params.set('email', this.currentUser.email);
    }

    const imageUrl = `${this.baseUrl}/imageRecord/v1/script/list`;
    const elementUrl = `${this.baseUrl}/imageRecord/v1/element/record/script/list`;

    forkJoin({
      imageScripts: this.http.get(imageUrl, { params, observe: 'response' }).pipe(
        map((res: HttpResponse<any>) => {
          if (res.status !== 200) {
            throw new Error(`Image scripts request returned status ${res.status}`);
          }
          return res.body;
        }),
        catchError((err: HttpErrorResponse | Error) => {
          const status = err instanceof HttpErrorResponse ? err.status : 'unknown';
          console.error('Failed to load image scripts', err);
          this.openAlertMod(this.alertTemplate, `Failed to load image scripts (status: ${status}).`);
          return of(null); // fallback so forkJoin doesn't die
        })
      ),
      elementScripts: this.http.get(elementUrl, { params, observe: 'response' }).pipe(
        map((res: HttpResponse<any>) => {
          if (res.status !== 200) {
            throw new Error(`Element scripts request returned status ${res.status}`);
          }
          return res.body;
        }),
        catchError((err: HttpErrorResponse | Error) => {
          const status = err instanceof HttpErrorResponse ? err.status : 'unknown';
          console.error('Failed to load element scripts', err);
          this.openAlertMod(this.alertTemplate, `Failed to load element scripts (status: ${status}).`);
          return of(null); // fallback so forkJoin doesn't die
        })
      )
    }).subscribe({
      next: (res: any) => {
        this.scriptsList = (res.imageScripts || []).map((s: any) => ({ ...s, scriptType: 'image' }));
        this.elementScriptsList = (res.elementScripts || []).map((s: any) => ({ ...s, scriptType: 'element' }));
        this.isScriptsLoading = false;

        const list = this.filteredTestCases;
        if (list.length > 0) {
          this.selectTestCase(list[0]);
        }
      },
      error: (err) => {
        // Only hit if something truly unexpected slips through both catchErrors
        console.error('Failed to load script lists', err);
        this.isScriptsLoading = false;
        this.openAlertMod(this.alertTemplate, 'Failed to load scripts. Please try again.');
      }
    });
  }

  // Combines scriptsList / elementScriptsList based on the active toggle
  get combinedScriptsList(): any[] {
    if (this.scriptListsOf === 'image') return this.scriptsList;
    if (this.scriptListsOf === 'element') return this.elementScriptsList;
    return [...this.scriptsList, ...this.elementScriptsList];
  }

  get filteredTestCases(): any[] {
    const list = this.combinedScriptsList;
    if (!this.tcSearchQuery) {
      return list;
    }
    return list.filter(s =>
      s.script_name?.toLowerCase().includes(this.tcSearchQuery.toLowerCase())
    );
  }

  // Called when the Image / Element / Both toggle changes
  setScriptListsOf(value: 'image' | 'element' | 'both') {
    if (this.scriptListsOf === value) return;
    this.scriptListsOf = value;
    this.onSearchChange();
  }

  onSearchChange() {
    const list = this.filteredTestCases;
    if (list.length > 0) {
      this.selectTestCase(list[0]);
    } else {
      this.selectedScript = null;
      this.selectedScriptDetails = null;
    }
  }

  // Resolves the correct API base for a given script depending on its source
  private getScriptApiBase(script: any): string {
    return script?.scriptType === 'element'
      ? `${this.baseUrl}/imageRecord/v1/element/record/script`
      : `${this.baseUrl}/imageRecord/v1/script`;
  }

  selectTestCase(script: any) {
    if (!script) return;
    this.selectScript(script);
  }

  selectScript(script: any) {
    if (!script?.script_id) return;
    this.selectedScript = script;
    this.scriptSteps = [];
    this.executionLiveStatus = '';
    const url = `${this.getScriptApiBase(script)}/${script.script_id}`;
    this.http.get(url).subscribe((res: any) => {
      if (res && res.steps) {
        this.selectedScriptDetails = { ...res, scriptType: script.scriptType };
        this.scriptSteps = res.steps.map((s: any) => ({
          ...s,
          status: 'NOT_RUN',
          logs: []
        }));
        res.steps.forEach((s: any) => {
          if (s.image_id) {
            this.loadStepImage(script.script_id, s.image_id);
          }
        });
      }
    });
  }

  loadStepImage(scriptId: string, imageId: string): void {
    if (this.loadedImages[imageId]) return;
    const url = `${this.baseUrl}/imageRecord/v1/image/`;
    const params = new HttpParams()
      .set('script_id', scriptId)
      .set('image_id', imageId);
    this.http.get(url, { params, responseType: 'blob' }).subscribe({
      next: (blob: Blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.loadedImages[imageId] = reader.result as string;
        };
        reader.readAsDataURL(blob);
      },
      error: () => {
        this.openAlertMod(this.alertTemplate, 'Failed to load image for step. Please check the server or network connection.');
      }
    });
  }

  toggleImagePreview(event: Event, imageId: string, scriptId: string, step: any): void {
    event.stopPropagation();
    if (!imageId) return;
    step.showImage = !step.showImage;
  }

  backToScriptsList() {
    this.selectedScript = null;
    this.scriptSteps = [];
    this.executionLiveStatus = '';
  }

  async runTestCase(script: any) {
    try {
      // Ensure we have an Initialization Phase step at the top
      if (!this.scriptSteps.find(s => s.step === -1)) {
        this.scriptSteps.unshift({
          step: -1,
          action: 'SYSTEM',
          element_name: 'Initialization Phase',
          status: 'NOT_RUN',
          logs: []
        });
      }

      this.scriptSteps = this.scriptSteps.map(s => ({
        ...s,
        status: s.step === -1 ? 'RUNNING' : 'NOT_RUN',
        logs: [],
        error: undefined
      }));
      this.expandedStepIndex = -1; // Auto-expand Initialization Phase
      this.executionLiveStatus = 'RUNNING';

      const response = script.scriptType === 'element'
        ? await this.playElementScript(script)
        : await this.playImageScript(script);

      if (response?.success && response?.execution_id) {
        this.currentExecutionId = response.execution_id;
        this.executionLiveStatus = 'RUNNING';
        // Add a 2-second delay to give the Python backend time to fully initialize 
        // the execution context and ws_manager before we attempt to connect.
        setTimeout(() => {
          this.initializeLiveWebSocket(response.execution_id);
        }, 2000);
      } else {
        this.executionLiveStatus = 'FAILED';
      }
    } catch (error) {
      this.executionLiveStatus = 'FAILED';
    }
  }

  private async playImageScript(script: any): Promise<any> {
    const dynamicCaps = {
      "platformName": this.device?.platform || "Android",
      "appium:automationName": this.device?.platform === "Android" ? "uiautomator2" : "XCUITest",
      "appium:platformVersion": this.device?.version || "",
      "appium:deviceName": this.device?.serial || "",
      "appium:udid": this.device?.serial || "",
      "appium:apmosys.user": this.device?.owner?.email || "",
      "appium:apmosys.accessToken": this.Token || ""
    };

    const playUrl = `${this.baseUrl}/imageRecord/v1/script/play`;
    const payload = {
      script_id: script.script_id,
      udid: this.device?.serial || '',
      capabilities: this.deviceCapabilities || dynamicCaps
    };

    try {
      const response = await firstValueFrom(
        this.http.post(playUrl, payload, { observe: 'response' })
      );
      if (response.status !== 200) {
        this.openAlertMod(this.alertTemplate, 'Failed to play image script: ' + response.statusText);
        return null;
      }
      return response.body;
    } catch (err) {
      this.openAlertMod(this.alertTemplate, 'Failed to play image script: ' + err);
      return null;
    }
  }

  private async playElementScript(script: any): Promise<any> {
    const dynamicCaps = {
      "platformName": this.device?.platform || "Android",
      "appium:automationName": this.device?.platform === "Android" ? "uiautomator2" : "XCUITest",
      "appium:platformVersion": this.device?.version || "",
      "appium:deviceName": this.device?.serial || "",
      "appium:udid": this.device?.serial || "",
      "appium:apmosys.user": this.device?.owner?.email || "",
      "appium:apmosys.accessToken": this.Token || ""
    };
    const playUrl = `${this.baseUrl}/imageRecord/v1/element/execute/script/play`;
    const payload = {
      script_id: script.script_id,
      udid: this.device?.serial || '',
      capabilities: this.deviceCapabilities || dynamicCaps
    };

    try {
      const response = await firstValueFrom(
        this.http.post(playUrl, payload, { observe: 'response' })
      );
      if (response.status !== 200) {
        this.openAlertMod(this.alertTemplate, 'Failed to play element script: ' + response.statusText);
        return null;
      }
      return response.body;
    } catch (err) {
      this.openAlertMod(this.alertTemplate, 'Failed to play element script: ' + err);
      return null;
    }
  }

  initializeLiveWebSocket(executionId: string) {
    let wsBaseUrl = this.baseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const wsUrl = `${wsBaseUrl}/imageRecord/v1/execution/ws/${executionId}`;
    console.log("Connecting to WebSocket:", wsUrl);
    this.liveWs = new WebSocket(wsUrl);

    this.liveWs.onopen = () => {
      console.log("WebSocket Connection Opened successfully.");
    };
    this.liveWs.onerror = (err) => console.error("WebSocket Error:", err);
    this.liveWs.onclose = (evt) => console.log("WebSocket Closed:", evt.code, evt.reason);

    this.liveWs.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Handle Overall Execution Status
        if (data.event === 'EXECUTION_SUCCESS') {
          this.executionLiveStatus = 'COMPLETED';
          // Mark any steps still RUNNING or NOT_RUN as SUCCESS
          this.scriptSteps = this.scriptSteps.map(s => ({
            ...s,
            status: (s.status === 'RUNNING' || s.status === 'NOT_RUN') ? 'SUCCESS' : s.status
          }));
        } else if (data.event === 'EXECUTION_FAILED') {
          this.executionLiveStatus = 'FAILED';

          if (data.reason) {
            const formattedReason = data.reason.replace(/Step (\d+)/g, (match: string, p1: string) => 'Step ' + (parseInt(p1, 10) + 1));
            const runningStep = this.scriptSteps.find(s => s.status === 'RUNNING' && s.step !== -1);
            if (runningStep) {
              runningStep.status = 'FAILED';
              runningStep.error = formattedReason;
              runningStep.logs.push(formattedReason);
              this.expandedStepIndex = runningStep.step;
              this.scrollToStep(runningStep.step);
            } else {
              const initStepIndex = this.scriptSteps.findIndex(s => s.step === -1);
              if (initStepIndex !== -1) {
                this.scriptSteps[initStepIndex].status = 'FAILED';
                this.scriptSteps[initStepIndex].error = formattedReason;
                this.scriptSteps[initStepIndex].logs.push(formattedReason);
                this.expandedStepIndex = -1;
                this.scrollToStep(-1);
              }
            }
          }

          // Mark any RUNNING steps as FAILED
          this.scriptSteps = this.scriptSteps.map(s => ({
            ...s,
            status: s.status === 'RUNNING' ? 'FAILED' : s.status
          }));
        }

        // Handle Step Level Status
        if (data.step !== undefined) {
          // Backend sends 0-based execution index. Map it to the actual step by skipping the Initialization Phase
          const realSteps = this.scriptSteps.filter(s => s.step !== -1);
          const targetStep = realSteps[data.step];
          const stepIndex = targetStep ? this.scriptSteps.indexOf(targetStep) : -1;

          if (stepIndex !== -1) {
            let newStatus = this.scriptSteps[stepIndex].status;

            if (data.event === 'ACTION_STARTED' || data.event === 'STEP_MATCHING' || data.event === 'STEP_RETRY') {
              newStatus = 'RUNNING';
              this.scrollToStep(targetStep.step);
            } else if (data.event === 'STEP_SUCCESS') {
              newStatus = 'SUCCESS';
            } else if (data.event === 'STEP_FAILED' || data.event === 'ACTION_FAILED') {
              newStatus = 'FAILED';
              if (data.reason) {
                const formattedReason = data.reason.replace(/Step (\d+)/g, (match: string, p1: string) => 'Step ' + (parseInt(p1, 10) + 1));
                this.scriptSteps[stepIndex].error = formattedReason;
                this.expandedStepIndex = targetStep.step; // Expand to show error details
                this.scrollToStep(targetStep.step);
              }
            }

            // If backend provides logs directly, use them. Otherwise push the raw event string for visibility.
            const newLogs = data.logs || [JSON.stringify(data)];

            this.scriptSteps[stepIndex] = {
              ...this.scriptSteps[stepIndex],
              status: newStatus,
              logs: [...(this.scriptSteps[stepIndex].logs || []), ...newLogs]
            };
          }
        } else if (data.event && !['EXECUTION_SUCCESS', 'EXECUTION_FAILED'].includes(data.event)) {
          // Handle Global/Initialization Events (no step attached)
          const initStepIndex = this.scriptSteps.findIndex(s => s.step === -1);
          if (initStepIndex !== -1) {
            let logMsg = `[${data.event}]`;
            if (data.count) logMsg += ` count: ${data.count}`;
            if (data.session_id) logMsg += ` Session: ${data.session_id}`;

            this.scriptSteps[initStepIndex].logs.push(logMsg);

            if (data.event === 'EXECUTION_RUNNING') {
              this.scriptSteps[initStepIndex].status = 'SUCCESS';
              this.scrollToStep(-1);
            }
          }
        }
      } catch (e) {
        console.error("Error parsing websocket message", e);
        this.openAlertMod(this.alertTemplate, 'Error parsing websocket message: ' + e);
      }
    };
  }

  getLogs(step: any): string[] {
    return step.logs || [];
  }

  scrollToStep(stepNumber: number) {
    setTimeout(() => {
      const container = document.querySelector('.tc-steps-wrap');
      const element = document.getElementById('step-' + stepNumber);

      if (container && element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Scroll so the step is near the top of the view (minus a small margin)
        const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 20;
        container.scrollTo({ top: scrollTop, behavior: 'smooth' });
      } else if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 250);
  }

  toggleStepExpand(index: number) {
    if (this.expandedStepIndex === index) {
      this.expandedStepIndex = null;
    } else {
      this.expandedStepIndex = index;
    }
  }

  passedStepsCount(): number {
    return this.scriptSteps.filter(s => s.status === 'SUCCESS' || s.status === 'PASSED').length;
  }

  openCapabilitiesEditor() {
    this.capEditAppName = this.deviceCapabilities?.['appium:appActivity'] || '';
    this.capEditPackageName = this.deviceCapabilities?.['appium:appPackage'] || '';

    let rawCaps = { ...this.deviceCapabilities };
    // We intentionally keep appActivity and appPackage in rawCaps so they display in the JSON editor

    if (Object.keys(rawCaps).length === 0) {
      rawCaps = {
        "platformName": this.device?.platform || "Android",
        "appium:automationName": this.device?.platform === "Android" ? "uiautomator2" : "XCUITest",
        "appium:platformVersion": this.device?.version || "",
        "appium:deviceName": this.device?.serial || "",
        "appium:udid": this.device?.serial || "",
      };
    }

    this.capEditRawJson = JSON.stringify(rawCaps, null, 2);
    this.capJsonError = '';

    this.capsDialogRef = this.dialog.open(this.capsModalTemplate, {
      width: '600px',
      disableClose: true,
      panelClass: 'capabilities-dialog'
    });
  }

  closeCapabilitiesEditor() {
    if (this.capsDialogRef) {
      this.capsDialogRef.close();
      this.capsDialogRef = null;
    }
  }

  fetchPackageDetails() {
    if (!this.device || !this.device.providerUrl || !this.device.serial) return;
    const url = `${this.device.providerUrl}/getPackageName/${this.device.serial}`;
    this.http.get(url).subscribe({
      next: (res: any) => {
        if (this.device.ios) {
          this.capEditPackageName = res.packageName || this.capEditPackageName;
        } else {
          this.capEditPackageName = res.appPackage || this.capEditPackageName;
          this.capEditAppName = res.appActivity || this.capEditAppName;
        }
        this.syncCapsToJson();
      },
      error: (err) => {
        console.error("Failed to fetch package details", err);
      }
    });
  }

  saveCapabilities(): void {
    if (this.capJsonError) return;
    try {
      // The JSON is already the source of truth if we use syncCapsToJson
      const parsed = JSON.parse(this.capEditRawJson);
      if (this.capEditAppName) parsed['appium:appActivity'] = this.capEditAppName;
      if (this.capEditPackageName) parsed['appium:appPackage'] = this.capEditPackageName;
      this.deviceCapabilities = parsed;
      if (this.capsDialogRef) {
        this.capsDialogRef.close();
      }
    } catch (e: any) {
      this.capJsonError = 'Error saving capabilities.';
    }
  }

  syncCapsToJson(): void {
    try {
      const parsed = JSON.parse(this.capEditRawJson);
      if (this.capEditAppName) {
        parsed['appium:appActivity'] = this.capEditAppName;
      } else {
        delete parsed['appium:appActivity'];
      }
      if (this.capEditPackageName) {
        parsed['appium:appPackage'] = this.capEditPackageName;
      } else {
        delete parsed['appium:appPackage'];
      }
      this.capEditRawJson = JSON.stringify(parsed, null, 2);
      this.capJsonError = '';
    } catch (e) {
      // JSON is currently invalid; wait until user fixes it
    }
  }

  onCapJsonChange(): void {
    try {
      JSON.parse(this.capEditRawJson);
      this.capJsonError = '';
    } catch (e: any) {
      this.capJsonError = 'Invalid JSON: ' + e.message;
    }
  }

  // --- Execution Control Methods ---

  async stopExecution() {
    if (!this.currentExecutionId) return;
    try {
      if (this.selectedScript?.scriptType === 'element') {
        const params = new HttpParams().set('execution_id', this.currentExecutionId);
        try {
          await this.http.post(`${this.baseUrl}/imageRecord/v1/element/execute/script/stop`, null, { params }).toPromise();
        }
        catch (e) {
          this.openAlertMod(this.alertTemplate, 'Failed to stop element script execution: ' + e);
        }
      } else {
        try {
          await this.http.post(`${this.baseUrl}/imageRecord/v1/script/stop`, { execution_id: this.currentExecutionId }).toPromise();
        } catch (e) {
          this.openAlertMod(this.alertTemplate, 'Failed to stop image script execution: ' + e);
        }
      }
      this.executionLiveStatus = 'STOPPED';
      if (this.liveWs) this.liveWs.close();
    } catch (e) {
      console.error("Failed to stop execution", e);
    }
  }

  async pauseExecution() {
    if (!this.currentExecutionId) return;
    try {
      await this.http.post(`${this.baseUrl}/imageRecord/v1/execution/${this.currentExecutionId}/pause`, {}).toPromise();
      this.executionLiveStatus = 'PAUSED';
    } catch (e) {
      console.error("Failed to pause execution", e);
      this.openAlertMod(this.alertTemplate, 'Failed to pause execution: ' + e);
    }
  }

  async resumeExecution() {
    if (!this.currentExecutionId) return;
    try {
      await this.http.post(`${this.baseUrl}/imageRecord/v1/execution/${this.currentExecutionId}/resume`, {}).toPromise();
      this.executionLiveStatus = 'RUNNING';
    } catch (e) {
      console.error("Failed to resume execution", e);
      this.openAlertMod(this.alertTemplate, 'Failed to resume execution: ' + e);
    }
  }

  // --- Script Management Methods ---

  async deleteScript(script: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Are you sure you want to delete script '${script.script_name}'?`)) return;

    try {
      await this.http.delete(`${this.getScriptApiBase(script)}/${script.script_id}`).toPromise();

      if (script.scriptType === 'element') {
        this.elementScriptsList = this.elementScriptsList.filter(s => s.script_id !== script.script_id);
      } else {
        this.scriptsList = this.scriptsList.filter(s => s.script_id !== script.script_id);
      }

      if (this.selectedScript?.script_id === script.script_id) {
        this.backToScriptsList();
      }
    } catch (e) {
      console.error("Failed to delete script", e);
      alert("Failed to delete script.");
    }
  }

  // --- Step Level Edit & Delete ---

  @ViewChild('editStepModalTemplate') editStepModalTemplate!: TemplateRef<any>;
  editStepDialogRef: any;
  editSingleStepJson: string = '';
  editSingleStepError: string = '';
  stepBeingEdited: any = null;

  async deleteStep(step: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`Are you sure you want to delete this step?`)) return;

    this.scriptSteps = this.scriptSteps.filter(s => s !== step);
    const realSteps = this.scriptSteps.filter(s => s.step !== -1);

    const payload = {
      script_name: this.selectedScript.script_name,
      mobile_name: this.selectedScript.mobile_name,
      steps: realSteps
    };
    try {
      await this.http.put(`${this.getScriptApiBase(this.selectedScript)}/${this.selectedScript.script_id}`, payload).toPromise();
    } catch (e) {
      alert("Failed to delete step on server.");
    }
  }

  openEditStepModal(step: any, event: Event) {
    event.stopPropagation();
    this.stepBeingEdited = step;

    const cleanStep = { ...step };
    delete cleanStep.status;
    delete cleanStep.logs;
    delete cleanStep.error;
    delete cleanStep.showImage;

    this.editSingleStepJson = JSON.stringify(cleanStep, null, 2);
    this.editSingleStepError = '';

    this.editStepDialogRef = this.dialog.open(this.editStepModalTemplate, {
      width: '600px',
      disableClose: true,
      panelClass: 'edit-script-dialog'
    });
  }

  closeEditStepModal() {
    if (this.editStepDialogRef) {
      this.editStepDialogRef.close();
      this.editStepDialogRef = null;
    }
  }

  onEditSingleStepJsonChange() {
    try {
      JSON.parse(this.editSingleStepJson);
      this.editSingleStepError = '';
    } catch (e: any) {
      this.editSingleStepError = 'Invalid JSON: ' + e.message;
    }
  }

  async saveSingleStep() {
    if (this.editSingleStepError) return;
    let parsedStep;
    try {
      parsedStep = JSON.parse(this.editSingleStepJson);
    } catch (e) {
      this.editSingleStepError = "Invalid JSON in Step";
      return;
    }

    Object.assign(this.stepBeingEdited, parsedStep);

    const realSteps = this.scriptSteps.filter(s => s.step !== -1).map(s => {
      const clean = { ...s };
      delete clean.status;
      delete clean.logs;
      delete clean.error;
      delete clean.showImage;
      return clean;
    });

    const payload = {
      script_name: this.selectedScript.script_name,
      mobile_name: this.selectedScript.mobile_name,
      steps: realSteps
    };

    try {
      await this.http.put(`${this.getScriptApiBase(this.selectedScript)}/${this.selectedScript.script_id}`, payload).toPromise();
      this.closeEditStepModal();
    } catch (e) {
      this.editSingleStepError = "Failed to save step to server.";
    }
  }

  async openEditScriptModal(script: any, event: Event) {
    event.stopPropagation();
    try {
      // Fetch details first if not already loaded
      const url = `${this.getScriptApiBase(script)}/${script.script_id}`;
      const res: any = await this.http.get(url).toPromise();

      this.scriptBeingEdited = { ...res, scriptType: script.scriptType };
      this.editScriptName = res.script_name;
      this.editScriptMobile = res.mobile_name || '';
      this.editScriptStepsJson = JSON.stringify(res.steps || [], null, 2);
      this.editScriptJsonError = '';

      this.editScriptDialogRef = this.dialog.open(this.editScriptModalTemplate, {
        width: '600px',
        disableClose: true,
        panelClass: 'capabilities-dialog'
      });
    } catch (error) {
      console.error("Failed to load script details for editing", error);
      alert("Failed to load script details.");
    }
  }

  closeEditScriptModal() {
    if (this.editScriptDialogRef) {
      this.editScriptDialogRef.close();
      this.editScriptDialogRef = null;
    }
  }

  onEditScriptJsonChange() {
    try {
      JSON.parse(this.editScriptStepsJson);
      this.editScriptJsonError = '';
    } catch (e: any) {
      this.editScriptJsonError = 'Invalid JSON: ' + e.message;
    }
  }

  async saveScriptDetails() {
    if (!this.scriptBeingEdited) return;
    if (this.editScriptJsonError) return;

    let parsedSteps = [];
    try {
      parsedSteps = JSON.parse(this.editScriptStepsJson);
    } catch (e) {
      this.editScriptJsonError = "Invalid JSON in Steps";
      return;
    }

    const payload = {
      script_name: this.editScriptName,
      mobile_name: this.editScriptMobile,
      steps: parsedSteps
    };
    try {
      await this.http.put(`${this.getScriptApiBase(this.scriptBeingEdited)}/${this.scriptBeingEdited.script_id}`, payload).toPromise();

      const targetList = this.scriptBeingEdited.scriptType === 'element' ? this.elementScriptsList : this.scriptsList;
      const idx = targetList.findIndex(s => s.script_id === this.scriptBeingEdited.script_id);
      if (idx !== -1) {
        targetList[idx].script_name = this.editScriptName;
      }

      // Update selected script details if it's the one being edited
      if (this.selectedScriptDetails && this.selectedScriptDetails.script_id === this.scriptBeingEdited.script_id) {
        this.selectedScriptDetails.script_name = this.editScriptName;
        this.selectedScriptDetails.mobile_name = this.editScriptMobile;
        this.selectedScriptDetails.steps = parsedSteps;

        this.scriptSteps = this.selectedScriptDetails.steps.map((s: any) => ({
          ...s,
          status: 'NOT_RUN',
          logs: []
        }));
      }

      this.closeEditScriptModal();
    } catch (e) {
      console.error("Failed to update script", e);
      alert("Failed to update script.");
    }
  }

  openAlertMod(template: TemplateRef<any>, message: any) {
    this.modalRef = this.dialog.open(template, { width: '300px' });
    this.msg = message;
    setTimeout(() => {
      this.modalRef.close();;
      this.msg = '';
    }, 2000)
  }

  Ok() {
    this.msg = '';
    this.modalRef.close();
  }
}